import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAndroidAppMode } from "@/hooks/useAndroidAppMode";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Smartphone, ArrowRight, MailCheck, FileDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { verifyAndGrantPremium, sendGuestMagicLinkForSession, getAddonGuideDownload } from "@/lib/checkout.functions";
import { track } from "@/lib/analytics";
import { remarketingPurchase } from "@/lib/remarketing";
import { isAppMode } from "@/lib/app-mode";
import { browserTrackingEnv, firePurchaseDestinations } from "@/lib/purchase-client-tracking";
import { DigitalServicesPromo } from "@/components/DigitalServicesPromo";

const search = (s: Record<string, unknown>): { days: number; session_id?: string } => {
  const n = Number(s.days);
  return {
    days: Number.isFinite(n) ? n : 7,
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  };
};

export const Route = createFileRoute("/success")({
  validateSearch: search,
  component: Success,
  head: () => ({
    meta: [
      { title: "Tack för ditt köp – Alkoholprovet" },
      { name: "description", content: "Bekräftelse på ditt köp av Alkoholprovet. Din tillgång aktiveras direkt och kvitto skickas via e-post." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Tack för ditt köp – Alkoholprovet" },
      { property: "og:description", content: "Vi bekräftar ditt köp och aktiverar din tillgång när betalningen är verifierad." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function Success() {
  const { t } = useTranslation("common");
  const { days, session_id } = Route.useSearch();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAndroidApp = useAndroidAppMode();
  useEffect(() => {
    if (isAndroidApp) nav({ to: "/dashboard", replace: true });
  }, [isAndroidApp, nav]);
  const verify = useServerFn(verifyAndGrantPremium);
  const sendGuestMagic = useServerFn(sendGuestMagicLinkForSession);
  const [activated, setActivated] = useState(false);
  const [purchaseEmailMasked, setPurchaseEmailMasked] = useState<string | null>(null);
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    const firePurchaseEvents = (result: {
      amount_total: number;
      currency: string;
      plan_label: string;
      price_id: string;
      payment_status: string;
    }) => {
      if (!session_id) return;
      if (result.payment_status !== "paid") return;
      try {
        // GA4 och Ads markeras var för sig först när eventet köats med samtycke.
        firePurchaseDestinations(
          {
            sessionId: session_id,
            paymentStatus: result.payment_status,
            amountTotalMinor: result.amount_total,
            currency: result.currency,
            itemId: result.price_id,
            itemName: result.plan_label,
          },
          browserTrackingEnv(isAppMode()),
          (value) => remarketingPurchase(result.price_id, value, session_id),
        );
      } catch { /* mätning får aldrig blockera */ }

      const plKey = `purchase_fired_${session_id}`;
      try {
        if (sessionStorage.getItem(plKey)) return;
        sessionStorage.setItem(plKey, "1");
      } catch { /* ignore */ }
      const valueSek = (result.amount_total ?? 0) / 100;
      const currency = (result.currency || "SEK").toUpperCase();
      track("Checkout Completed", {
        days,
        session_id,
        value: valueSek,
        currency,
        item_id: result.price_id,
        item_name: result.plan_label,
        payment_status: "succeeded",
      });
    };

    const loop = async () => {
      if (cancelled) return;

      let email = user?.email ?? null;
      if (session_id) {
        try {
          const res = await verify({ data: { sessionId: session_id } });
          if (res?.granted) {
            if (res.email_masked) setPurchaseEmailMasked(res.email_masked);
            setActivated(true);
            firePurchaseEvents({
              amount_total: res.amount_total ?? 0,
              currency: res.currency ?? "SEK",
              plan_label: res.plan_label ?? `Premium ${days} dagar`,
              price_id: res.price_id ?? (days <= 10 ? "premium_10d_once" : days >= 90 ? "premium_90d_once" : "premium_30d_once_v2"),
              payment_status: res.payment_status ?? "paid",
            });
            return;
          }
        } catch (e) {
          console.error("verifyAndGrantPremium failed", e);
        }
      }

      if (email && (await checkDb(email))) {
        setActivated(true);
        return;
      }

      if (++tries < 15 && !cancelled) setTimeout(loop, 1500);
    };

    const checkDb = async (email: string) => {
      const { data } = await supabase
        .from("premium_access")
        .select("active_until")
        .eq("email", email.toLowerCase())
        .gte("active_until", new Date().toISOString())
        .limit(1)
        .maybeSingle();
      return !!data;
    };

    loop();
    return () => { cancelled = true; };
  }, [user?.email, session_id, verify, days]);


  const sendMagicLink = async () => {
    if (!session_id) return;
    setMagicSending(true);
    setMagicError(null);
    try {
      const res = await sendGuestMagic({
        data: {
          sessionId: session_id,
          redirectTo: window.location.origin + "/dashboard",
        },
      });
      setMagicSending(false);
      if (res.sent) {
        setMagicSent(true);
      } else if ("reason" in res && res.reason === "rate_limited") {
        const seconds = "retry_after_seconds" in res ? res.retry_after_seconds : 60;
        setMagicError(
          t("cSuccess.guestMagicRateLimited", {
            defaultValue:
              "En inloggningslänk har precis skickats. Kolla din inkorg – eller försök igen om {{seconds}} sekunder.",
            seconds,
          }),
        );
      } else {
        setMagicError(t("cSuccess.guestMagicError"));
      }
    } catch (e: any) {
      setMagicSending(false);
      setMagicError(e?.message ?? t("cSuccess.genericError"));
    }
  };

  // Tillvalet "Checklista & snabbguide" – visas bara om det ingick i köpet.
  const guideDownload = useServerFn(getAddonGuideDownload);
  const [guideAvailable, setGuideAvailable] = useState(false);
  const [guideLoading, setGuideLoading] = useState(false);
  useEffect(() => {
    if (!activated || !session_id) return;
    let cancelled = false;
    let n = 0;
    const check = async () => {
      try {
        const r = await guideDownload({ data: { sessionId: session_id } });
        if (cancelled) return;
        if (r.url) { setGuideAvailable(true); return; }
      } catch { /* ignore */ }
      if (++n < 4 && !cancelled) setTimeout(check, 2000);
    };
    check();
    return () => { cancelled = true; };
  }, [activated, session_id, guideDownload]);
  const downloadGuide = async () => {
    if (!session_id) return;
    setGuideLoading(true);
    try {
      const r = await guideDownload({ data: { sessionId: session_id } });
      if (r.url) {
        track("Addon Downloaded", { addon: "provdag_guide" });
        window.location.href = r.url;
      }
    } finally {
      setGuideLoading(false);
    }
  };

  const isGuest = !user && activated && !!purchaseEmailMasked;


  return (
    <div className="container-narrow py-20 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-success/15 text-success">
        <CheckCircle2 className="h-8 w-8" />
      </div>
      <h1 className="mt-5 font-display text-4xl font-bold">{t("cSuccess.title")}</h1>
      <p className="mt-3 text-muted-foreground">
        {t("cSuccess.lead", { days })}
      </p>
      {!activated && (
        <p className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> {t("cSuccess.activating")}
        </p>
      )}

      {isGuest && (
        <div className="mt-8 mx-auto max-w-xl rounded-2xl border-2 border-primary bg-primary/5 p-6 text-left shadow-elevated">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <MailCheck className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary">{t("cSuccess.nextStep")}</div>
              <h2 className="mt-1 font-display text-xl font-bold">{t("cSuccess.loginTitle")}</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("cSuccess.loginBody", { email: purchaseEmailMasked })}
              </p>
              {magicSent ? (
                <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-success/10 px-4 py-2 text-sm text-success">
                  <CheckCircle2 className="h-4 w-4" /> {t("cSuccess.sentLink")}
                </p>
              ) : (
                <button
                  onClick={sendMagicLink}
                  disabled={magicSending}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                >
                  {magicSending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("cSuccess.sendLink")}
                </button>
              )}
              {magicError && <p className="mt-2 text-xs text-destructive">{magicError}</p>}
            </div>
          </div>
        </div>
      )}

      {guideAvailable && (
        <div className="mt-8 mx-auto max-w-xl rounded-2xl border-2 border-success bg-success/5 p-6 text-left shadow-elevated">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-success text-success-foreground">
              <FileDown className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold">
                {t("cSuccess.guideTitle", { defaultValue: "Din checklista & snabbguide" })}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("cSuccess.guideBody", { defaultValue: "Spara PDF:en i mobilen – den är gjord för sista veckan och själva provdagen." })}
              </p>
              <button
                onClick={downloadGuide}
                disabled={guideLoading}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-success px-5 py-2.5 text-sm font-semibold text-success-foreground hover:bg-success/90 disabled:opacity-60"
              >
                {guideLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                {t("cSuccess.guideBtn", { defaultValue: "Ladda ner PDF" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 mx-auto max-w-xl rounded-2xl border-2 border-accent bg-gradient-to-br from-accent/10 to-primary/5 p-6 text-left shadow-elevated">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <Smartphone className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-accent">{t("cSuccess.stepPromo")}</div>
            <h2 className="mt-1 font-display text-xl font-bold">{t("cSuccess.installTitle")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("cSuccess.installBody")}
            </p>
            <Link
              to="/installera"
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground hover:bg-accent/90"
            >
              {t("cSuccess.installBtn")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {user && (
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/practice" className="rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">{t("cSuccess.practiceNow")}</Link>
          <Link to="/dashboard" className="rounded-xl border border-border px-6 py-3 text-sm hover:bg-secondary">{t("cSuccess.dashboardNow")}</Link>
        </div>
      )}

      {!isAndroidApp && (
        <div className="mx-auto mt-10 max-w-2xl">
          <DigitalServicesPromo />
        </div>
      )}
    </div>
  );
}
